import React, { useState, useMemo } from 'react';
import { X, FileSpreadsheet, Layers, Image as ImageIcon, Loader2, CheckCircle, Download, ArrowRight, AlertTriangle, ImageOff, Package } from 'lucide-react';
import { MenuData } from '../types';
import type { MenuProfile } from '../types';
import { downloadImagesZip } from '../services/imageService';
import { getMediaUrl } from '../../../core/fileUpload';
import { orchestrateExport } from '../services/ExportOrchestrator';
import { exportMenuToExcelBuffer } from '../services/MenuExcelEngine';
import { exportBulkPackage } from '../services/BulkOnboardingService';

interface ExportModalProps {
  isOpen: boolean;
  data: MenuData;
  /** Raw profile for relational shared-modifier analysis */
  profile?: MenuProfile;
  /** Currency symbol for Excel templates */
  currency?: string;
  onClose: () => void;
  onExportTemplate1: () => void;
  onExportTemplate2: () => void;
  onExportBoth: () => void;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen, data, profile, currency, onClose, onExportTemplate1, onExportTemplate2, onExportBoth
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);

  // Detect shared modifiers from profile
  const sharedCount = useMemo(() => {
    if (!profile) return 0;
    try {
      const orch = orchestrateExport(profile);
      return orch.sharedGroups.length;
    } catch { return 0; }
  }, [profile]);

  const stats = useMemo(() => {
    const cats = data.categories.length;
    let items = 0, images = 0, addOns = 0;
    const catsWithoutImages: string[] = [];
    data.categories.forEach(c => {
      let catHasImage = false;
      c.items.forEach(i => {
        items++;
        if (i.image) { images++; catHasImage = true; }
        addOns += i.choiceGroups.length;
      });
      if (!catHasImage && c.items.length > 0) catsWithoutImages.push(c.name.en);
    });
    return { cats, items, images, addOns, catsWithoutImages };
  }, [data]);

  const hasImages = stats.images > 0;
  const hasPartialImages = hasImages && stats.catsWithoutImages.length > 0;

  const handleDownloadImages = async () => {
    if (!hasImages) return;
    setIsDownloading(true); setIsComplete(false); setProgress(0);
    try {
      await downloadImagesZip(data, (p) => setProgress(p), { profile, currency });
      setIsComplete(true);
      setTimeout(() => { setIsDownloading(false); setIsComplete(false); onClose(); }, 1500);
    } catch (error) { setIsDownloading(false); }
  };

  const handleBulkPackage = async () => {
    if (!profile) return;
    setIsDownloading(true); setIsComplete(false); setProgress(0);
    try {
      const excelBuffer = exportMenuToExcelBuffer({ profile, includeOptions: true, withImageFilenames: true });
      await exportBulkPackage({
        profile,
        excelBuffer,
        onProgress: (p) => setProgress(p),
      });
      setIsComplete(true);
      setTimeout(() => { setIsDownloading(false); setIsComplete(false); onClose(); }, 1500);
    } catch (error) { setIsDownloading(false); }
  };

  if (!isOpen) return null;

  const excelOptions: { key: string; onClick: () => void; icon: any; accent: string; hoverBorder: string; hoverBg: string; title: string; sub: string }[] = [
    { key: 't1', onClick: onExportTemplate1, icon: FileSpreadsheet, accent: 'bg-amber-500', hoverBorder: 'hover:border-amber-300', hoverBg: 'hover:bg-amber-50/50', title: 'Template 1 — Detailed', sub: sharedCount > 0 ? `Yellow bars · Unique options inline · ${sharedCount} shared groups on Sheet 2` : 'Yellow bars · Option groups · Min/Max' },
    { key: 't2', onClick: onExportTemplate2, icon: FileSpreadsheet, accent: 'bg-purple-500', hoverBorder: 'hover:border-purple-300', hoverBg: 'hover:bg-purple-50/50', title: 'Template 2 — Aggregator', sub: sharedCount > 0 ? `Purple header · Shared groups as [SHARED] refs · ${sharedCount} groups on Sheet 2` : 'Purple header · Flat list · Add-ons inline' },
    { key: 'both', onClick: onExportBoth, icon: Layers, accent: 'bg-sky-500', hoverBorder: 'hover:border-sky-300', hoverBg: 'hover:bg-sky-50/50', title: 'Both Templates', sub: sharedCount > 0 ? `Detailed + Aggregator + Shared Modifier Library` : '2 sheets in one .xlsx file' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden relative" onClick={e => e.stopPropagation()}>

        {/* Download overlay */}
        {isDownloading && (
          <div className="absolute inset-0 bg-white/95 z-50 flex flex-col items-center justify-center p-6 rounded-xl">
            {isComplete ? (
              <>
                <CheckCircle size={28} className="text-emerald-500 mb-2" />
                <p className="text-sm font-semibold text-slate-700">Download Complete!</p>
                {hasPartialImages && (
                  <p className="text-[10px] text-amber-500 mt-1 text-center">Only categories with images were included</p>
                )}
              </>
            ) : (
              <>
                <Loader2 size={28} className="text-orange-500 animate-spin mb-2" />
                <p className="text-sm font-semibold text-slate-700 mb-2">Preparing Images...</p>
                <div className="w-full max-w-[200px] bg-slate-100 rounded-full h-1.5 mb-1">
                  <div className="bg-gradient-to-r from-orange-400 to-amber-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-slate-400">{progress}%</p>
              </>
            )}
          </div>
        )}

        {/* Header */}
        <div className="relative">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 via-purple-500 to-emerald-500" />
          <div className="px-4 pt-3.5 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              {data.logo ? (
                <img loading="lazy" src={getMediaUrl(data.logo)} alt="" className="w-8 h-8 rounded-md object-cover border border-slate-200 flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Download size={14} className="text-slate-400" />
                </div>
              )}
              <div className="min-w-0">
                <h3 className="font-bold text-slate-800 text-sm leading-tight">Export Menu</h3>
                <p className="text-[11px] text-slate-400 truncate leading-tight mt-0.5">
                  {data.brandName && <span className="font-medium text-slate-500">{data.brandName}</span>}
                  {data.brandName && data.title?.en && ' · '}
                  {data.title?.en}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex-shrink-0">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
            {[
              { label: 'Categories', val: stats.cats, color: 'text-amber-600' },
              { label: 'Items', val: stats.items, color: 'text-purple-600' },
              { label: 'Images', val: stats.images, color: 'text-emerald-600' },
              { label: 'Add-ons', val: stats.addOns, color: 'text-sky-600' },
              ...(sharedCount > 0 ? [{ label: 'Shared Groups', val: sharedCount, color: 'text-teal-600' }] : []),
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center">
                <span className={`text-sm font-bold ${s.color}`}>{s.val}</span>
                <span className="text-[9px] text-slate-400 uppercase tracking-wider font-medium">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Excel export options */}
        <div className="px-4 pb-2 space-y-1.5">
          {excelOptions.map(opt => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.key}
                onClick={opt.onClick}
                className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 ${opt.hoverBorder} ${opt.hoverBg} hover:shadow-sm transition-all duration-150 text-left`}
              >
                <div className={`flex-shrink-0 w-7 h-7 rounded-md ${opt.accent} flex items-center justify-center`}>
                  <Icon size={14} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-xs text-slate-700">{opt.title}</div>
                  <div className="text-[10px] text-slate-400 leading-tight">{opt.sub}</div>
                </div>
                <ArrowRight size={14} className="text-slate-200 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
              </button>
            );
          })}
        </div>

        {/* Image & Bulk package section */}
        <div className="px-4 pb-4 pt-1.5">
          <div className="border-t border-slate-100 pt-2.5 space-y-1.5">
            {/* Bulk Complete Package */}
            {hasImages && profile && (
              <button
                onClick={handleBulkPackage}
                className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50/50 hover:shadow-sm transition-all duration-150 text-left"
              >
                <div className="flex-shrink-0 w-7 h-7 rounded-md bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                  <Package size={14} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-xs text-slate-700">Complete Package (.zip)</div>
                  <div className="text-[10px] text-slate-400 leading-tight">Excel + {stats.images} images · Re-importable bulk package</div>
                </div>
                <ArrowRight size={14} className="text-slate-200 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
              </button>
            )}

            {/* Images-only download */}
            {hasImages ? (
              <>
                <button
                  onClick={handleDownloadImages}
                  className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 hover:shadow-sm transition-all duration-150 text-left"
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-md bg-emerald-500 flex items-center justify-center">
                    <ImageIcon size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs text-slate-700">Download Images</div>
                    <div className="text-[10px] text-slate-400 leading-tight">ZIP by category · {stats.images} images</div>
                  </div>
                  <ArrowRight size={14} className="text-slate-200 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
                </button>
                {hasPartialImages && (
                  <div className="flex items-start gap-1.5 mt-1.5 px-1">
                    <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-600 leading-tight">
                      No images in: {stats.catsWithoutImages.join(', ')} — only categories with images will be downloaded
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-slate-200 bg-slate-50/50">
                <div className="flex-shrink-0 w-7 h-7 rounded-md bg-slate-200 flex items-center justify-center">
                  <ImageOff size={14} className="text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-xs text-slate-400">No Images Available</div>
                  <div className="text-[10px] text-slate-400 leading-tight">This menu has no item images to download</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

